/**
 * Stakko event bus.
 *
 * - `on/emit` funguje v rámci jedné stránky (React strom, moduly).
 * - `BroadcastChannel` synchronizuje stejný origin mezi taby.
 * - Pro skutečně cross-app události použij `publishNotification` ve
 *   `services/notifications.ts` (Firestore je jediná společná vrstva, protože
 *   každá aplikace běží na jiné doméně).
 */

type StakkoHandler<T = unknown> = (payload: T) => void;

interface StakkoBusMessage {
  type: string;
  payload?: unknown;
}

class StakkoBus {
  private listeners = new Map<string, Set<StakkoHandler>>();
  private channel: BroadcastChannel | null = null;
  private started = false;

  private start(): void {
    if (this.started || typeof window === 'undefined') return;
    this.started = true;

    if ('BroadcastChannel' in window) {
      try {
        this.channel = new BroadcastChannel('stakko-core');
        this.channel.onmessage = (event: MessageEvent<StakkoBusMessage>) => {
          const data = event.data;
          if (data && typeof data.type === 'string') {
            this.dispatch(data.type, data.payload);
          }
        };
      } catch {
        this.channel = null;
      }
    }
  }

  on<T = unknown>(type: string, handler: StakkoHandler<T>): () => void {
    this.start();
    let set = this.listeners.get(type);
    if (!set) {
      set = new Set();
      this.listeners.set(type, set);
    }
    set.add(handler as StakkoHandler);

    return () => {
      const current = this.listeners.get(type);
      if (!current) return;
      current.delete(handler as StakkoHandler);
      if (current.size === 0) this.listeners.delete(type);
    };
  }

  emit<T = unknown>(type: string, payload?: T, broadcast = true): void {
    this.start();
    this.dispatch(type, payload);
    if (broadcast && this.channel) {
      try {
        this.channel.postMessage({ type, payload } satisfies StakkoBusMessage);
      } catch {
        /* payload nemusí být serializovatelný – lokální dispatch už proběhl */
      }
    }
  }

  private dispatch(type: string, payload?: unknown): void {
    const set = this.listeners.get(type);
    if (!set) return;
    for (const handler of Array.from(set)) {
      try {
        handler(payload);
      } catch (error) {
        console.warn('[stakko] handler selhal', type, error);
      }
    }
  }
}

export const stakkoBus = new StakkoBus();

export const STAKKO_EVENTS = {
  authChanged: 'stakko:auth',
  walletChanged: 'stakko:wallet',
  entitlementsChanged: 'stakko:entitlements',
  notificationsChanged: 'stakko:notifications',
  creditsSpent: 'stakko:credits-spent',
  creditsEarned: 'stakko:credits-earned',
  openPanel: 'stakko:open-panel',
  buyCredits: 'stakko:buy-credits',
} as const;
